import React, { useState, useEffect } from 'react';
import {
    Tabs,
    Tab,
    Box,
    Typography,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useMediaQuery,
    MenuItem,
    Select,
    FormControl,
} from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import { useTheme } from '@mui/material/styles';
import { Helmet } from 'react-helmet';

const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`}>
        {value === index && (
            <Box sx={{ p: 3 }}>
                <Typography>{children}</Typography>
            </Box>
        )}
    </div>
);

const Download = () => {
    const theme = useTheme();
    const [tabIndex, setTabIndex] = useState(0);
    // const [macTabIndex, setMacTabIndex] = useState(0);
    const [releases, setReleases] = useState([]);
    const [selectedRelease, setSelectedRelease] = useState('');
    const [osData, setOsData] = useState({ mac: [], windows: [], linux: [] });
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        const fetchReleases = async () => {
            try {
                const response = await fetch(
                    'https://api.github.com/repos/prashantrathi123/hawkClient/releases'
                );
                const data = await response.json();
                setReleases(data);
                if (data.length > 0) {
                    setSelectedRelease(data[0].tag_name); // Default to the latest release
                    updateOsData(data[0]);
                }
            } catch (error) {
                console.error('Error fetching releases:', error);
            }
        };

        fetchReleases();
    }, []);

    const updateOsData = (release) => {
        const mac = [];
        const windows = [];
        const linux = [];

        release.assets.forEach((asset) => {
            if (asset.name.includes('mac')) {
                mac.push({ os: asset.name, version: release.tag_name, link: asset.browser_download_url });
            } else if (asset.name.includes('win')) {
                windows.push({ os: asset.name, version: release.tag_name, link: asset.browser_download_url });
            } else if (asset.name.includes('linux')) {
                linux.push({ os: asset.name, version: release.tag_name, link: asset.browser_download_url });
            }
        });

        setOsData({ mac, windows, linux });
    };

    const handleReleaseChange = (event) => {
        const release = releases.find((r) => r.tag_name === event.target.value);
        setSelectedRelease(event.target.value);
        updateOsData(release);
    };

    const renderTable = (data) => (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
                <TableHead>
                    <TableRow
                        sx={{
                            backgroundColor: theme.palette.grey[700],
                            color: '#fff',
                            '& .MuiTableCell-root': {
                                color: '#fff',
                                fontWeight: 'bold',
                            },
                        }}
                    >
                        <TableCell>OS</TableCell>
                        <TableCell>Version</TableCell>
                        <TableCell>Download</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((row, index) => (
                        <TableRow key={index}>
                            <TableCell>{row.os}</TableCell>
                            <TableCell>{row.version}</TableCell>
                            <TableCell>
                                <Link href={row.link} download>
                                    Download
                                </Link>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <>
            <Helmet>
                <title>Download - HawkClient</title>
                <meta
                    name="description"
                    content="Download HawkClient today, a developer-friendly offline-only API client."
                />
            </Helmet>
            <Box sx={{ padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '100vh' }}>
                {/* Release Dropdown */}

                {/* Horizontal Tabs */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', mt: 2 }}>
                    <Typography variant="h5" component="h1">
                        Download the HawkClient app today
                    </Typography>
                    <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', gap: isMobile ? '8px' : '80px', justifyContent: 'center' }}>
                        <FormControl sx={{ width: '150px', flexGrow: 1 }}>
                            {/* <InputLabel id="release-select-label">Select Release</InputLabel> */}
                            <Select
                                id="release-select"
                                value={selectedRelease}
                                // variant="standard"
                                onChange={handleReleaseChange}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        borderBottom: 'none', // Remove the bottom border
                                    },
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 48 * 4.5 + 8, // Approx. 4 items with padding
                                            width: 250, // Set the width if needed
                                        },
                                    },
                                }}
                            >
                                {releases.map((release) => (
                                    <MenuItem key={release.id} value={release.tag_name}>
                                        {release.tag_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Tabs
                            value={tabIndex}
                            onChange={(event, newIndex) => setTabIndex(newIndex)}
                            centered
                            textColor="primary"
                            indicatorColor="primary"
                        >
                            <Tab label="Mac" icon={<AppleIcon />} iconPosition="start" />
                            <Tab label="Windows" icon={<MicrosoftIcon />} iconPosition="start" />
                            <Tab label="Linux" icon={<DesktopWindowsIcon />} iconPosition="start" />
                        </Tabs>
                    </Box>
                </Box>

                {/* Tab Panels */}
                <Box sx={{ width: { md: '70%' } }}>
                    {/* Mac Tab Panel */}
                    <TabPanel value={tabIndex} index={0}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
                            <Typography variant="h6">Download for Mac</Typography>
                            {renderTable(osData.mac)}
                            {/* <Box>
                                <Typography variant="body1" align="center" sx={{ mt: 2 }}>
                                    This app is not notarized yet due to that reason once this app is opened on mac user might see below error.
                                </Typography>
                                <img
                                    src="/download/MacNotarizeError.png"
                                    alt="MacNotarizeError"
                                    style={{
                                        width: isMediumScreen ? '100%' : '60%',
                                        // maxWidth: '800px',
                                        height: 'auto',
                                        display: 'block',
                                        objectFit: 'contain',
                                        marginTop: '16px',
                                    }}
                                />
                                <Typography variant="body1" align="center" sx={{ mt: 2 }}>
                                    Solution: to resolve this issue user have to enable <strong>Anywhere</strong> option that allows to install app that are not yet notarised. To do this user can execute the below command in terminal
                                </Typography>
                                <Box
                                    component="pre"
                                    sx={{
                                        backgroundColor: '#f5f5f5',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        mt: 2,
                                        overflow: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        textAlign: 'left',
                                    }}
                                >
                                    <Typography component="code" sx={{ color: '#d32f2f' }}>
                                        sudo spctl --master-disable
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography component="code" >
                                After this the <strong>Anywhere</strong> option will be selected under <strong>Privacy and Security</strong> as shown below
                            </Typography>
                            <img
                                src="/download/AnywhereOption.png"
                                alt="AnywhereOption"
                                style={{
                                    width: isMediumScreen ? '100%' : '60%',
                                    // maxWidth: '800px',
                                    height: 'auto',
                                    display: 'block',
                                    objectFit: 'contain',
                                    marginTop: '16px',
                                }}
                            />
                            <Typography component="body1" >
                                Now user can open the app by clicking <strong>Open Anyway</strong> button corresponding to hawkclient as shown in above pic, it will show a warning user can click on open. This activity is one time and user can use the app afterwards without any issues
                            </Typography> */}
                        </Box>
                    </TabPanel>

                    {/* Windows Tab Panel */}
                    <TabPanel value={tabIndex} index={1}>
                        <Box sx={{ display: 'flex', mt: 2 }}>
                            <Box sx={{ flexGrow: 1, p: 2 }}>
                                <Typography variant="h6">Download for Windows</Typography>
                                {renderTable(osData.windows)}
                            </Box>
                        </Box>
                    </TabPanel>

                    {/* Linux Tab Panel */}
                    <TabPanel value={tabIndex} index={2}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
                            <Box sx={{ flexGrow: 1, p: 2 }}>
                                <Typography variant="h6">Download for Linux</Typography>
                                {renderTable(osData.linux)}
                            </Box>
                        </Box>
                    </TabPanel>
                </Box>
            </Box>
        </>
    );
};

export default Download;
